'use client';

import { CalendarDays, CircleAlert, X } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  formatWallClock,
  isCalendarDay,
  isWallClock,
  joinWallClock,
  parseWallClockInput,
  wallClockDay,
  wallClockTime,
  type CalendarDay,
  type PickerMode,
} from '@icebreaker/shared/domain';
import { useTranslation } from './i18n-context';
import { CalendarPanel } from './date-time-calendar';
import { useDialogFocus } from './focus-contract';
import styles from './date-time-picker.module.css';

/** Hata cümlesindeki örnek — biçim locale'e göre yazılır, dize gömülmez. */
const EXAMPLE = '2026-09-12T18:30';

/**
 * Çağıran dilim vermediyse tarayıcının TAKVİM günü (UTC dilimi DEĞİL).
 * Zaman kaynağı `Date.now` — `new Date()` lint kuralıyla yasak (base.js).
 */
function browserToday(): CalendarDay {
  const now = new Date(Date.now());
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export interface DateTimePickerProps {
  /** `Field` etiketinin hedefi — metin girişine verilir. */
  readonly id: string;
  /**
   * DUVAR SAATİ: `YYYY-MM-DDTHH:mm` (datetime) ya da `YYYY-MM-DD` (date).
   * ISO damgası DEĞİLDİR; çağıran `isoToWallClock` / `wallClockToIso` ile
   * etkinliğin dilimi üzerinden çevirir (bkz. `packages/shared/.../wall-clock.ts`).
   */
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly mode?: PickerMode;
  /** Seçilebilir en erken değer (aynı biçim). Geçmiş tarih kuralı buradan gelir. */
  readonly min?: string;
  /**
   * Seçilebilir en geç değer (aynı biçim). Ajanda oturumu etkinliğin bitişini
   * aşamaz (elle test B-10): sonraki günler kapalı, son günde bitiş saatinin
   * üstündeki saatler kapalıdır.
   */
  readonly max?: string;
  /** Bugünün günü, etkinliğin diliminde. Verilmezse tarayıcının günü. */
  readonly today?: CalendarDay;
  /** Alanın altında yazılan dilim notu; verilmezse yazılmaz. */
  readonly timezone?: string;
  readonly minuteStep?: number;
  /** Gün seçilip saat henüz yoksa kullanılacak saat. */
  readonly defaultTime?: string;
  readonly invalid?: boolean;
  readonly disabled?: boolean;
  readonly required?: boolean;
  /** Filtre alanlarında değeri boşaltma düğmesi. */
  readonly clearable?: boolean;
  /** Görünür etiketi olmayan alanlarda (ör. aralığın ikinci ucu) erişilebilir ad. */
  readonly ariaLabel?: string;
  /**
   * Alanın DIŞINDA basılan hata/ipucu metninin kimliği (ör. iki alanı birlikte
   * anlatan zamanlama hatası). Çözülemeyen metin cümlesiyle birlikte okunur.
   */
  readonly describedBy?: string;
}

/**
 * ÜRÜNÜN KENDİ TARİH/SAAT SEÇİCİSİ (elle test 2 md. 12/17 · elle test 3).
 *
 * NEDEN: yerel `datetime-local` üç kez şikâyet konusu oldu — takvim küçük bir
 * ikonun arkasındaydı, görünüm işletim sistemine göre değişiyordu, TR biçimi ve
 * hafta başlangıcı garanti değildi. Ayrıca değeri TARAYICININ dilimiyle
 * yorumluyordu; etkinliğin kendi dilimi varken bu sessiz kayma üretiyordu.
 *
 * METİN GİRİŞİ KALDIRILMADI: ekran okuyucu ve klavye kullanıcısı için alan hâlâ
 * yazılabilir ve locale sırasıyla ayrıştırılır (`parseWallClockInput`). Şikâyet
 * "yazılabiliyor" değil "seçilemiyor"du; takvim artık eşit haklı ikinci yoldur.
 *
 * DURUMLAR: boş (placeholder YOK — kullanıcı kararı 30.08: maske hem uzun hem
 *   dar kolonda kırpılıyordu; biçim ipucu gerekiyorsa çağıran `Field` hint'i
 *   kullanır) · hata (çözülemeyen metin cümlesi + kırmızı
 * kenarlık) · devre dışı (sunken zemin, işaretçi yok) · kapalı gün (geçmiş).
 */
export function DateTimePicker({
  id,
  value,
  onChange,
  mode = 'datetime',
  min,
  max,
  today,
  timezone,
  minuteStep = 15,
  defaultTime = '09:00',
  invalid,
  disabled,
  required,
  clearable,
  ariaLabel,
  describedBy,
}: DateTimePickerProps) {
  const t = useTranslation();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => formatWallClock(value, t.locale, mode));
  const [unparsable, setUnparsable] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  /*
   * KATMAN GÖVDEYE TAŞINIR (kullanıcı bulgusu 30.08).
   *
   * Panel `position: absolute` iken en yakın kaydırılan/kırpan atayı aşamıyordu:
   * keşif sayfasının filtre kartı kendi içinde kaydırdığı için takvim KIRPILIYOR,
   * haftanın son iki günü görünmüyordu. Konum artık alanın ekrandaki yerinden
   * hesaplanır ve panel `document.body` altına çizilir; hiçbir ata onu kesemez.
   */
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);

  /*
   * Konum ölçümü: alan açıldığında ve sayfa kaydırılıp yeniden boyutlandığında.
   * `useLayoutEffect` — boyama öncesi ölçülür, panel yanlış yerde bir kare parlamaz.
   * Aşağı sığmıyorsa yukarı açılır; sağdan taşarsa görünüm alanına çekilir.
   */
  useLayoutEffect(() => {
    if (!open) {
      setAnchor(null);
      return;
    }
    const konumla = () => {
      const alan = wrapperRef.current;
      const katman = popoverRef.current;
      if (!alan) return;
      const kutu = alan.getBoundingClientRect();
      const katmanYuksekligi = katman?.offsetHeight ?? 0;
      const katmanGenisligi = katman?.offsetWidth ?? 0;
      const bosluk = 6;
      const altaSigar = kutu.bottom + bosluk + katmanYuksekligi <= window.innerHeight;
      const top = altaSigar
        ? kutu.bottom + bosluk
        : Math.max(bosluk, kutu.top - bosluk - katmanYuksekligi);
      const enSag = window.innerWidth - katmanGenisligi - bosluk;
      const left = Math.max(bosluk, Math.min(kutu.left, enSag));
      setAnchor({ top, left });
    };
    konumla();
    window.addEventListener('resize', konumla);
    window.addEventListener('scroll', konumla, true);
    return () => {
      window.removeEventListener('resize', konumla);
      window.removeEventListener('scroll', konumla, true);
    };
  }, [open]);

  /* Dışarıdan gelen değer (sunucudan yükleme, sıfırlama) metni TAZELER — ama
     kullanıcı alanın içindeyken yazdığını ezmez. */
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setText(formatWallClock(value, t.locale, mode));
    setUnparsable(false);
  }, [value, t.locale, mode]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (pointer: MouseEvent) => {
      const target = pointer.target;
      if (target instanceof Node && wrapperRef.current?.contains(target)) return;
      // Panel artık gövdede; tıklama sarmalayıcının içinde görünmez.
      if (target instanceof Node && popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  /*
   * ODAK SÖZLEŞMESİ (tech-lead ölçümü 30.08 — kırmızı kapı).
   *
   * Katman gövdeye taşındığı için Tab sırasında FORMUN SONUNDA duruyordu:
   * takvim açıldıktan sonra ızgaraya varmak 12 Tab sürüyordu ve tek çıkış
   * Esc'ti. Katman artık gerçek bir diyalog gibi davranır — odak açılışta
   * içeri girer, Tab içeride sarılır, kapanışta tetikleyiciye döner.
   *
   * ODAK PANELİN KENDİSİNE OTURUR, hücreye değil: paneldeki duraklar sırasıyla
   * "Önceki ay → Sonraki ay → gezinen gün → gezinen saat"tır. Hücreye
   * oturulsaydı klavye kullanıcısı ay oklarına ancak sarmalı geçerek ulaşırdı;
   * kanıt vakası da (`e2e/tarih-secici-kanit.spec.ts` C) bu sırayı ölçüyor.
   * Panel `tabIndex={-1}` taşır: odak alır, Tab sırasını şişirmez.
   *
   * `ready`: katman ölçülene kadar `visibility: hidden` durur ve GÖRÜNMEYEN
   * öğe gerçek tarayıcıda odak ALMAZ — odak konum yazıldıktan sonra taşınır.
   */
  useDialogFocus({
    open,
    panelRef: popoverRef,
    onEscape: () => setOpen(false),
    ready: anchor !== null,
    returnFocusTo: toggleRef,
  });

  /*
   * ESC YALNIZ PANELİ KAPATIR (Modal içindeki alan, 22.10.2026). Modal da Esc'i
   * pencere üzerinde dinler ve önce açıldığı için ÖNCE koşuyordu: tek Esc tüm
   * katmanı kapatıyor, girilen değer kayboluyordu. Panel açıkken Esc pencerenin
   * YAKALAMA evresinde alınır ve yayılımı durdurulur; hiçbir dinleyiciye
   * (Modal, `useDialogFocus`) ulaşmaz. Panel kapalıyken dinleyici yoktur ve Esc
   * Modal'ı yine kapatır.
   */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== 'Escape') return;
      keyEvent.stopPropagation();
      keyEvent.preventDefault();
      setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  const closePanel = () => setOpen(false);

  const commitText = (next: string) => {
    setText(next);
    if (next.trim() === '') {
      setUnparsable(false);
      onChange('');
      return;
    }
    const parsed = parseWallClockInput(next, t.locale, mode);
    setUnparsable(parsed === null);
    if (parsed !== null) onChange(parsed);
  };

  const withTime = mode !== 'date';
  const selectedDay =
    mode === 'time'
      ? ''
      : mode === 'date'
        ? isCalendarDay(value)
          ? value
          : ''
        : wallClockDay(value);
  const selectedTime =
    mode === 'time' ? value : mode === 'datetime' && isWallClock(value) ? wallClockTime(value) : '';
  const minDay = mode === 'time' || !min ? undefined : min.slice(0, 10);
  const minTime =
    mode === 'time'
      ? min
      : mode === 'datetime' && min && min.length >= 16
        ? min.slice(11, 16)
        : undefined;
  const maxDay = mode === 'time' || !max ? undefined : max.slice(0, 10);
  const maxTime =
    mode === 'time'
      ? max
      : mode === 'datetime' && max && max.length >= 16
        ? max.slice(11, 16)
        : undefined;

  /** Gün seçilip saat yoksa önerilen saat, o günün alt/üst sınırına çekilir. */
  const suggestedTime = (day: CalendarDay): string => {
    if (minDay !== undefined && day === minDay && minTime && defaultTime < minTime) return minTime;
    if (maxDay !== undefined && day === maxDay && maxTime && defaultTime > maxTime) return maxTime;
    return defaultTime;
  };

  const pickDay = (day: CalendarDay) => {
    if (mode === 'date') {
      onChange(day);
      setText(formatWallClock(day, t.locale, mode));
      setUnparsable(false);
      closePanel();
      return;
    }
    const time = selectedTime || suggestedTime(day);
    const next = joinWallClock(day, time);
    onChange(next);
    setText(formatWallClock(next, t.locale, mode));
    setUnparsable(false);
  };

  const pickTime = (time: string) => {
    if (mode === 'time') {
      onChange(time);
      setText(time);
      setUnparsable(false);
      closePanel();
      return;
    }
    const day = selectedDay || today || '';
    if (!day) return;
    const next = joinWallClock(day, time);
    onChange(next);
    setText(formatWallClock(next, t.locale, mode));
    setUnparsable(false);
    closePanel();
  };

  const showError = invalid || unparsable;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div
        className={[
          styles.control,
          showError ? styles.controlError : '',
          disabled ? styles.controlDisabled : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <input
          ref={inputRef}
          id={id}
          className={styles.text}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={text}
          disabled={disabled}
          required={required}
          aria-label={ariaLabel}
          aria-invalid={showError || undefined}
          aria-describedby={
            [unparsable ? `${panelId}-err` : '', describedBy ?? ''].filter(Boolean).join(' ') ||
            undefined
          }
          onChange={(changeEvent) => commitText(changeEvent.target.value)}
          onBlur={() => setText(value ? formatWallClock(value, t.locale, mode) : text)}
        />
        {clearable && value && !disabled ? (
          <button
            type="button"
            className={`${styles.iconButton} ${styles.clearButton}`}
            aria-label={t.t('datePicker.clear')}
            onClick={() => {
              onChange('');
              setText('');
              setUnparsable(false);
            }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        ) : null}
        <button
          ref={toggleRef}
          type="button"
          className={`${styles.iconButton} ${styles.toggleHit}`}
          aria-label={t.t('datePicker.openCalendar')}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={open ? panelId : undefined}
          disabled={disabled}
          onClick={() => setOpen((previous) => !previous)}
        >
          <CalendarDays size={18} aria-hidden="true" />
        </button>
      </div>
      {unparsable ? (
        <p id={`${panelId}-err`} className={styles.hintError} role="alert">
          <CircleAlert className={styles.hintErrorIcon} aria-hidden="true" />
          {t.t('datePicker.unparsable', { example: formatWallClock(EXAMPLE, t.locale, mode) })}
        </p>
      ) : null}
      {timezone ? (
        <p className={styles.zoneNote}>{t.t('datePicker.timezoneNote', { timezone })}</p>
      ) : null}
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popoverRef}
              id={panelId}
              role="dialog"
              aria-modal="true"
              aria-label={t.t('datePicker.calendarLabel')}
              tabIndex={-1}
              className={styles.popover}
              /* Konum gövde koordinatında; ölçüm bitene kadar görünmez tutulur
                 ki panel yanlış yerde bir kare parlamasın. */
              style={{
                top: anchor?.top ?? 0,
                left: anchor?.left ?? 0,
                visibility: anchor ? 'visible' : 'hidden',
              }}
            >
              <CalendarPanel
                showCalendar={mode !== 'time'}
                selectedDay={selectedDay}
                todayDay={today ?? browserToday()}
                minDay={minDay}
                maxDay={maxDay}
                onPickDay={pickDay}
                time={withTime ? selectedTime : undefined}
                minuteStep={minuteStep}
                minTime={minTime}
                maxTime={maxTime}
                onPickTime={withTime ? pickTime : undefined}
                onClose={closePanel}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
